import React, {FC, useEffect, useMemo, useState} from "react";
import { useTranslation } from "react-i18next";
import { icons } from "../../../icons";
import { Button, FilterBar, ModalVariables, SearchComponent, StartPage, TopProgress} from "../../../components";
import Illustration from './illustration.svg'
import IllustrationDark from './illustration-dark.svg'
import { useNavigate } from "react-router";
import { useQueryParam, StringParam, NumberParam } from 'use-query-params';
import { useUi } from "../../../context/ui.context";
import { floatingIpService } from "../../../api";
import {
  StateWrapper,
  GreenDot,
  Greentext,
} from './styles'
import { useAuth } from "../../../context/user.context";
import { handleStatusName } from "../../../utils/handle-status-name";
import rootConfig from "../../../config";
import { compareArrays } from "../../../utils/findIntersection";
import { timeConverterMMDDYYHHMM } from "../../../utils/dates";
import useUpdateEffect from "../../../hooks/useUpdateEffect";

const FLOATING_IP_FILTER_KEYS = [
  'created_at',
  'updated_at',
  'project__service__client',
  'status',
];
const DEFAULT_ORDERING = 'floating_ip_address';

const FloatingIPs:FC = () => {
  const [ips, setIps] = useState<any[]>([])
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [searchString, setSearchString] = useQueryParam('search', StringParam);
  const [filtering, setFiltering] = useQueryParam('filtering', StringParam);
  const {user, quotas} = useAuth()


  const [paginationData, setPaginationData] = useState<any>()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>()
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [deletedGroup, setDeletedGroup] = useState<any>()
  const [editedIp, setEditedIp] = useState<any>()
  const [allChecked, setAllChecked] = useState<boolean>(false)
  const [checked, setChecked] = useState<any[]>([])
  const [loadingComplete, setLoadingComplete] = useState<boolean>(false)
  const [filterOptions, setFilterOptions] = useState<any>()
  const {
    setViewModalComplete,
    setActiveAnimationComplete,
    viewAlert
  } = useUi()

  const normalizedFilterOptions = useMemo(() => {
    if (!filterOptions) return undefined;
    return FLOATING_IP_FILTER_KEYS.reduce((acc:any, key) => {
      if (filterOptions?.[key]?.length) {
        acc[key] = filterOptions[key];
      }
      return acc;
    }, {});
  }, [filterOptions]);

  const handleResetFilters = () => {
    setSearchString(undefined);
    setFiltering(undefined);
    setOrdering(DEFAULT_ORDERING);
    setPage(1);
    setAllChecked(false);
    setChecked([]);
  }
  const editChecked = (id:any, isAll?:boolean) => {
    if(id === null) {
      setChecked([])
      return
    }
    const founded = checked.find((item:any) => item.id === id?.id)
    if(founded) {
      if(isAll){ 
        return 
      } else {
        setChecked((prev:any) => {
          return prev.filter((item:any) => item.id !== id?.id)
        })
        setAllChecked(false)
      }
    } else {
      setChecked((prev) => [...prev, id])
    }
  } 
  useEffect(() => {
    if(compareArrays(ips, checked) && checked?.length !== 0){
      setAllChecked(true)
    }
  },[checked])
  useEffect(() => {
    if (!ordering) {
      setOrdering(DEFAULT_ORDERING);
    }
  }, [ordering, setOrdering]);
  const [t] = useTranslation()
  const navigate = useNavigate()

  const _updateIps = () => {
    floatingIpService.getIps({
      page:page||1,
      ordering: ordering || DEFAULT_ORDERING,
      pageSize:pageSize||10,
      search:searchString||'',
      filtering: filtering || '',
    })
    .then((res:any) => {
      setFilterOptions(res?.data?.filter_options);
      setPaginationData(res.data)
      setIps(res.data.objects.map((ip:any) => {
        const associatedObject = ip.associated_object?.name || ip.associated_object?.id;
        return{
          ...ip,
          floating_ip_address:ip.floating_ip_address,
          floating_network:ip.floating_network?.name,
          associated_object:associatedObject,
          region:ip.floating_network?.region,
          client:ip.client?.name,
          created_at: timeConverterMMDDYYHHMM(ip.created_at),
          status: 
          (<StateWrapper>
            <GreenDot active={ip.status === "ACTIVE"}/>
            <Greentext active={ip.status === "ACTIVE"}>
              {handleStatusName(ip.status, t)}
            </Greentext>
          </StateWrapper>),
        }
      }))
    })
    .catch((e) => {
      viewAlert({
        severity:'error',
        message:e?.response?.data?.detail || `${t("ERROR_OCCURRED")}`,
      })
      navigate('/home')
    })
    .finally(() => {
      setPageLoading(false)
      setLoading(false)
    })
  }
  useEffect(() => {
    _updateIps()
    const interval = setInterval(() => {
      _updateIps()
    },10000)
    return () => {
      clearInterval(interval)
    }
  },[
    page,
    ordering,
    pageSize,
    searchString,
    filtering
  ])
  useUpdateEffect(() => {
    setPage(1)
  }, [pageSize, filtering])
  const openModal = (modal:any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }
  const _deleteGroups = async () => {
    setLoading(true)
    try{
      if(allChecked) {
        let promises:any[] = []
        await ips.map((item:any) => {
          let promise = floatingIpService.deleteIp(item.id)
          promises.push(promise)
        })
        await Promise.all(promises)
      } else if (checked?.length > 0){
        let promises:any[] = []
        await checked.map((item:any) => {
          let promise = floatingIpService.deleteIp(item.id)
          promises.push(promise)
        })
        await Promise.all(promises)
      } else if (deletedGroup) {
        await floatingIpService.deleteIp(deletedGroup.id)
      }
    } catch (e:any) {
      if(e?.response?.data.detail){
        viewAlert({
          severity:'error',
          message:e.response.data.detail,
        })
      }
      setPageLoading(false)
      setLoading(false)
    } finally {
      _updateIps()
      setViewModal(false)
      setChecked([])
      setAllChecked(false)
      setModalType('')
      setActiveAnimation(false)
    }
  }
  const closeModalCancel = () => {
    setViewModal(false)
    setEditedIp(null)
    setModalType('')
    setActiveAnimation(false)
  }
  const items = [
    {
      title:t("SET_PTR"),
      id:'2',
      icon:icons.Edit,
      onClick: (ip:any) => {
        openModal('set-ptr')
        setEditedIp(ip)
      },
      divider:false
    },
    {
      title:t("DELETE"),
      id:'4',
      onClick:(ip:any) => {
        openModal('delete')
        setAlertTitle(t("FLOATING_IP"))
        setAlertSubTitle([ip])
        setDeletedGroup(ip)
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]
  const Columns = [
    {
      title:t("IP_ADDRESS"),
      key:'floating_ip_address',
      sorted:true,
    },
    {
      title:t("STATUS"),
      key:'status',
      sorted:true,
    },
    {
      title:t("NETWORK"),
      key:'floating_network',
      sorted:true,
    },
    {
      title:t("ASSOCIATED_OBJECT"),
      key:'associated_object',
      sorted:true,
    },
    {
      title:t("PLACEMENT_REGION"),
      key:'region'
    },
    {
      title:t("CLIENT"),
      key:'client'
    },
    {
      title:t("CREATED_AT"),
      key:'created_at',
      sorted:true,
    },
    {
      title:'',
      key:'props'
    },
  ]
  
  
  const closeModal = (props?:any) => {
    if(modalType === 'delete'){
      _deleteGroups()
    } else if (modalType === 'create-floating-ip'){
      setLoading(true)
      floatingIpService.createFloatingIP(props)
      .then((res:any) => {
        setLoadingComplete(true)
        setTimeout(() => {
          _updateIps()
          setLoading(false)
          setLoadingComplete(false)
          setViewModal(false)
          setActiveAnimation(false)
          setModalType('')
        },1000)
      })
      .catch((err) => {
        viewAlert({
          severity:'error',
          message: err?.response?.data?.detail || `${t("ERROR_OCCURRED")}`
        })
        setLoading(false)
      })
    } else {
      setViewModal(false)
      setActiveAnimation(false)
      setModalType('')
    }
  }
  const _createFunc = () => {
    openModal('create-floating-ip')
  }

  return (
    <>
      <TopProgress loading={loading}/>
      <StartPage
        title={`${t("FLOATING_IPS")}`}
        noResourceText={`${t("YOU_HAVE_NO_FLOATING_IPS")}`}
        createResourceButton={`${t('CREATE_FLOATING_IP')}`}
        createResourceButtonEventOrLink={_createFunc}
        haveResource={ips?.length > 0}
        pageLoading={pageLoading}
        learnMoreAboutResource={`${t('LEARN_MORE_FLOATING_IP')}`}
        howCreateVideoText={`${t('HOW_CREATE_FLOATING_IP')}`}
        learnMoreAboutResourceLink={`${rootConfig.docs_url}category/floating-ips`}
        howCreateVideoUrl="https://www.youtube.com/embed/3ffI9KM4pbs"
        videoImage={Illustration}
        videoImageDark={IllustrationDark}
        withoutCreateResourceButton
        rightCustomCreateButton={
          <div style={{display:'flex', flexDirection:'column', gap:'10px', width:'100%'}}>
            <div style={{display:'flex', alignItems:'center', gap:'12px', width:'100%'}}>
              <Button
                title={t('CREATE_FLOATING_IP')}
                onClick={_createFunc}
                variant='primary'
                size='custom'
                customStyles={{height:'40px', whiteSpace:'nowrap'}}
              />
              <div style={{flex:1, minWidth:'200px'}}>
                <SearchComponent />
              </div>
              <div style={{display:'flex', alignItems:'center', gap:'8px', marginLeft:'auto'}}>
                <Button
                  onClick={() => {
                    setAlertTitle(t("FLOATING_IPS"))
                    setAlertSubTitle(allChecked ? ips : checked)
                    openModal('delete')
                  }}
                  variant='icon'
                  size='display'
                  test_id='button-delete'
                  disabled={!allChecked && checked?.length === 0 }
                >
                  <img src={icons.DeleteIcon}/>
                </Button>
                <Button
                  title={t("CLEAR_FILTERS")}
                  onClick={handleResetFilters}
                  variant='stroke'
                  size='display'
                  disabled={
                    (!filtering || filtering === '') &&
                    !searchString &&
                    ordering === DEFAULT_ORDERING
                  }
                />
              </div>
            </div>
            {normalizedFilterOptions ? (
              <FilterBar filterOptions={normalizedFilterOptions} />
            ) : null}
          </div>
        }
        tableData={{
          columns:Columns,
          data:ips,
          paginationData:paginationData,
          props:items,
          allChecked:allChecked,
          setAllChecked:setAllChecked,
          checked:checked,
          setChecked:editChecked,
          updateData:_updateIps
        }}
        hideQuotas
      />
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        loading={loading}
        loadingComplete={loadingComplete}
        alertSubTitle={alertSubTitle}
        dataToEdit={editedIp}
      />
    </>
  )
}


export default FloatingIPs
